import { useCallback, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import api from '@/api/client'

/**
 * Announce changes made to a project OUTSIDE the browser.
 *
 * Phase 4 of docs/ssh_agent_console_support_plan.md. "He logs in to Twasta to
 * see the changes" was only half-true: an edit made over SSH, from a laptop
 * agent, or by a `git pull` appeared silently on the next page load — no event,
 * no diff, no audit entry — and a concurrent browser save would overwrite it
 * with no conflict detection at all.
 *
 * On project open this asks the backend what moved since the last scan and, if
 * anything did, re-uses the SAME `twasta:agent-files-changed` event the dock
 * already dispatches — so every designer's existing `useAgentRefresh` /
 * `useAgentDeletion` wiring picks it up with no per-surface plumbing.
 *
 * Deliberately quiet in two cases:
 *  - the FIRST scan of a project (there is no baseline, so nothing "changed" —
 *    announcing the whole project as new work would be noise); and
 *  - when nothing changed, which is the overwhelmingly common case.
 */
export function useOutOfBandChanges(project: string | undefined): () => void {
  // Only announce once per project per mount: the check runs on open, and a
  // re-render must not re-toast the same change.
  const announcedRef = useRef<string | null>(null)

  const check = useCallback(async () => {
    if (!project) return
    try {
      const { data } = await api.get('/agent-access/changes', {
        params: { project, refresh: true },
      })
      if (data?.baseline || !data?.changed) return

      const who = data.attributed_to
      const byWhom = who
        ? `${who.display_name || who.principal} (${who.surface})`
        : 'someone working outside the browser'

      toast(
        `This project changed since you last opened it — ${data.summary}, by ${byWhom}. Reloading the affected views.`,
        { id: `oob-${project}`, icon: '🔄', duration: 8000 },
      )

      // Same event, same shape as the dock's — so useAgentRefresh reloads and
      // useAgentDeletion evicts, with no new listeners anywhere.
      window.dispatchEvent(
        new CustomEvent('twasta:agent-files-changed', {
          detail: {
            project,
            files: data.files || [],
            deleted: data.deleted || [],
          },
        }),
      )
    } catch {
      // Silent: this is an enhancement, and a failed check must never block
      // opening a project.
    }
  }, [project])

  useEffect(() => {
    if (!project || announcedRef.current === project) return
    announcedRef.current = project
    void check()
  }, [project, check])

  // Returned so a "refresh" affordance can re-run it on demand.
  return check
}
